import { useCallback, useRef } from 'react';

interface UseFocusInputOptions {
  /**
   * Strategy for finding inputs:
   * - 'name': Uses input name attribute (like DraggableFields)
   * - 'ref': Uses stored refs (like TreeTest)
   */
  strategy?: 'name' | 'ref';

  /**
   * Timeout for MutationObserver in milliseconds
   */
  timeout?: number;
}

export const useFocusInput = (options: UseFocusInputOptions = {}) => {
  const { strategy = 'name', timeout = 5000 } = options;
  const inputRefs = useRef<Record<string, HTMLInputElement>>({});

  const focusInput = useCallback(
    (fieldIdentifier: string) => {
      if (strategy === 'ref') {
        // First check if the ref already exists
        const inputElement = inputRefs.current[fieldIdentifier];
        if (inputElement && !inputElement.disabled) {
          requestAnimationFrame(() => {
            inputElement.focus();
          });
          return;
        }
      }

      // Fallback to DOM query (works for both strategies)
      const querySelector =
        strategy === 'name'
          ? `input[name="${fieldIdentifier}"]`
          : `input[data-testid="${fieldIdentifier}"], input[name="${fieldIdentifier}"]`;

      const existingInput = document.querySelector(querySelector) as HTMLInputElement;

      if (existingInput && !existingInput.disabled) {
        requestAnimationFrame(() => {
          existingInput.focus();
        });
        return;
      }

      // If input doesn't exist yet, use MutationObserver to wait for it
      const observer = new MutationObserver(() => {
        let inputElement: HTMLInputElement | null = null;

        if (strategy === 'ref') {
          inputElement = inputRefs.current[fieldIdentifier];
        }

        if (!inputElement) {
          inputElement = document.querySelector(querySelector) as HTMLInputElement;
        }

        if (inputElement && !inputElement.disabled) {
          requestAnimationFrame(() => {
            inputElement!.focus();
          });
          observer.disconnect();
        }
      });

      // Watch for changes in the DOM that might add new inputs
      observer.observe(document.body, { childList: true, subtree: true });

      // Cleanup observer after timeout to prevent memory leaks
      setTimeout(() => {
        observer.disconnect();
      }, timeout);
    },
    [strategy, timeout]
  );

  /**
   * Creates a ref callback for storing input refs (only needed when strategy is 'ref')
   */
  const createInputRefCallback = useCallback(
    (fieldIdentifier: string) => {
      if (strategy !== 'ref') {
        return undefined;
      }

      return (ref: HTMLInputElement | null) => {
        if (ref) {
          inputRefs.current[fieldIdentifier] = ref;
        } else {
          delete inputRefs.current[fieldIdentifier];
        }
      };
    },
    [strategy]
  );

  return {
    focusInput,
    createInputRefCallback: strategy === 'ref' ? createInputRefCallback : undefined,
    inputRefs: inputRefs.current
  };
};
